import React, { useState, useRef, useEffect } from "react";
import { Check, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { scoutAgentService } from "../../services/scoutAgentService";
import {
  SCOUT_PRIORITIES,
  SCOUT_LABEL_COLORS,
  SCOUT_LABEL_COLOR_ORDER,
} from "../../constants/scoutRequestChoices";

// Priority + custom-label menu (design 5b / 6a). Everything here is AGENCY-INTERNAL
// (Invariant 2) — the header says so explicitly. Priority is 1|2|3 (or cleared);
// custom labels are the agency's shared set, multiple per request. Each change is a
// single setMark PUT; the server returns the resolved mark, which we bubble up so
// the card repaints without a refetch.
const PriorityLabelMenu = ({ requestId, mark, labels, onClose, onMarkChange, onCreateLabel }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("blue");

  const currentLabelIds = (mark.labels || []).map((l) => l.id);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  const commit = async (priority, labelIds) => {
    setBusy(true);
    const res = await scoutAgentService.setMark(requestId, { priority, labelIds });
    setBusy(false);
    if (res.success) {
      onMarkChange(requestId, res.data);
    } else {
      toast.error(res.error || "Could not update this request.");
    }
    return res;
  };

  const setPriority = (value) => {
    // Toggle off when tapping the active priority; otherwise set it.
    const next = mark.priority === value ? null : value;
    commit(next, currentLabelIds);
  };

  const removePriority = () => commit(null, currentLabelIds);

  const toggleLabel = (labelId) => {
    const next = currentLabelIds.includes(labelId)
      ? currentLabelIds.filter((id) => id !== labelId)
      : [...currentLabelIds, labelId];
    commit(mark.priority, next);
  };

  const addLabel = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    const res = await onCreateLabel({ name, color: newColor });
    setBusy(false);
    if (res.success) {
      setNewName("");
      setNewColor("blue");
      commit(mark.priority, [...currentLabelIds, res.data.id]);
    } else {
      toast.error(res.error || "Could not create the label.");
    }
  };

  return (
    <div
      ref={ref}
      className="absolute left-0 top-8 z-30 w-64 bg-white border border-[#E5E7EB] rounded-[10px] shadow-[0_8px_24px_rgba(17,24,39,0.12)] py-2"
      data-testid="priority-label-menu"
    >
      {/* PRIORITY */}
      <p className="px-3 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-[0.07em] text-[#9CA3AF]">
        Priority · only your agency
      </p>
      {SCOUT_PRIORITIES.map((p) => {
        const active = mark.priority === p.value;
        return (
          <button
            key={p.value}
            onClick={() => setPriority(p.value)}
            disabled={busy}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#111827] hover:bg-gray-50 disabled:opacity-50"
          >
            <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ backgroundColor: p.dot }} />
            <span className="font-semibold">{p.label}</span>
            <span className="text-[#9CA3AF]">{p.verb}</span>
            {active && <Check className="w-4 h-4 text-[#1B6FE0] ml-auto" />}
          </button>
        );
      })}
      {mark.priority && (
        <button
          onClick={removePriority}
          disabled={busy}
          className="w-full text-left px-3 py-1.5 text-[13px] text-[#6B7280] hover:bg-gray-50 disabled:opacity-50"
        >
          Remove label
        </button>
      )}

      <div className="my-1.5 border-t border-[#E5E7EB]" />

      {/* AGENCY LABELS */}
      <p className="px-3 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-[0.07em] text-[#9CA3AF]">
        Agency labels
      </p>
      {labels?.length === 0 && (
        <p className="px-3 pb-1 text-[12px] text-[#9CA3AF]">No labels yet. Add one below.</p>
      )}
      {labels?.map((l) => {
        const checked = currentLabelIds.includes(l.id);
        return (
          <button
            key={l.id}
            onClick={() => toggleLabel(l.id)}
            disabled={busy}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-[#111827] hover:bg-gray-50 disabled:opacity-50"
          >
            <span
              className="w-[7px] h-[7px] rounded-full flex-shrink-0"
              style={{ backgroundColor: SCOUT_LABEL_COLORS[l.color] || SCOUT_LABEL_COLORS.blue }}
            />
            <span className="truncate">{l.name}</span>
            {checked && <Check className="w-4 h-4 text-[#1B6FE0] ml-auto flex-shrink-0" />}
          </button>
        );
      })}

      {/* New-label composer */}
      <div className="px-3 pt-2">
        <div className="flex items-center gap-1.5">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addLabel()}
            placeholder="U21 tournament"
            maxLength={40}
            className="flex-1 min-w-0 bg-[#F3F4F6] rounded-md px-2.5 py-1.5 text-[12.5px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
          />
          <button
            onClick={addLabel}
            disabled={busy || !newName.trim()}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#2283FF] text-white text-[12px] font-semibold hover:bg-[#1B6FE0] disabled:opacity-50 flex-shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
        <div className="flex items-center gap-2 mt-2">
          {SCOUT_LABEL_COLOR_ORDER.map((key) => (
            <button
              key={key}
              onClick={() => setNewColor(key)}
              aria-label={`${key} label colour`}
              className="w-5 h-5 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: SCOUT_LABEL_COLORS[key],
                outline: newColor === key ? "2px solid #111827" : "none",
                outlineOffset: "1px",
              }}
            >
              {newColor === key && <Check className="w-3 h-3 text-white" />}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-2 pb-1">Shared with your agency</p>
      </div>
    </div>
  );
};

export default PriorityLabelMenu;
